import { FC, memo, useEffect, useState } from 'react';
import sortBy from 'lodash/sortBy';
import classnames from 'classnames';
import { useRecoilValue } from 'recoil';
import { GameBookLocationType, PageData, Props } from './types';

import { translationDataState } from '../../../../store';
import Icon from '@/units/Icon';
import { Loader } from '@/units/Iframe/style';
import { Content, Footer, Header, List, Main, Root } from '@/components/tour/game/gameBook/styled';
import { BannerHorizontalViewOnly } from '../BannerHorizontalViewOnly/BannerHorizontalViewOnly';
import { parseTourLink } from '@/_helpers/base';
import { ClickAwayListener } from '@mui/material';
import { TourType } from '@/_models/Tour';
import { dLog } from '@/_helpers/renderDebug';
import DeviceManager from '@/components/util/deviceManager';

const GameBook: FC<Props & { children?: any }> = ({
  onClose,
  book,
  unlockedChapterIds,
  tryButtonPlaySound,
  show,
  moveToSweep,
  tourType,
}) => {
  const { backgroundColor, backgroundOpacity, textColor, buttonColor, chapterLocked } = book;

  if (!show) return null;

  const [activePage, setActivePage] = useState<PageData>();
  const [pages, setPages] = useState<PageData[]>([]);
  const translation = useRecoilValue(translationDataState);

  useEffect(() => {
    if (book?.chapters?.length > 0) {
      const data: PageData[] = sortBy(book.chapters, 'order').reduce(
        (acc, chapter) => {
          acc.push({
            id: chapter.id,
            title: chapter.title,
            description: chapter.description,
            url: chapter.img
              ? `${process.env.NEXT_PUBLIC_CDN}/games/${book.gameId}/book/${book.id}/chapter/${chapter.id}/${chapter.img}`
              : undefined,
            moveToSweepUuid: chapter.sweepUuid,
            locationType: chapter?.locationType,
            locationTourLink: chapter?.locationTourLink,
          });
          return acc;
        },
        [
          {
            id: 0,
            title: translation?.experienceCover || 'Cover',
            description: book.description,
            url: book.img
              ? `${process.env.NEXT_PUBLIC_CDN}/games/${book.gameId}/book/${book.id}/${book.img}`
              : undefined,
            moveToSweepUuid: undefined,
            locationType: undefined,
            locationTourLink: undefined,
          },
        ],
      );
      setPages(data);
      setActivePage(data[0]);
    }
  }, [book]);

  useEffect(() => {
    handleMoveToSweep(true);
  }, [activePage]);

  const isPageLocked = (chapterId: number) => {
    if (chapterId === 0 || !chapterLocked) return false;
    return !unlockedChapterIds.includes(chapterId);
  };

  const changePage = async (page: PageData) => {
    if (!isPageLocked(page.id)) {
      await tryButtonPlaySound();
      setActivePage(page);
    }
  };

  const handleMoveToSweep = (first = false) => {
    try {
      if (activePage && ((first && !activePage.url) || !first)) {
        if (activePage.locationType === GameBookLocationType.sweep && activePage.moveToSweepUuid) {
          moveToSweep(activePage.moveToSweepUuid);
        } else if (
          activePage.locationType === GameBookLocationType.tourLink &&
          activePage.locationTourLink
        ) {
          const parsedLink = parseTourLink(activePage.locationTourLink);
          if (parsedLink) {
            moveToSweep(parsedLink.s, { x: parsedLink.x, y: parsedLink.y });
          }
        } else if (activePage.id !== 0) {
          onClose();
        }
      }
    } catch (err) {
      console.error('gameBook handleMoveToSweep error');
    }
  };

  //timeout will prevent reopen configs closed by sidebar configs icon
  const handleClickAway = (ev) => {
    if (tourType === TourType.ECommerce) {
      window.setTimeout(() => {
        onClose();
      }, 100);
    }
  };
  dLog('GameBook');
  //disable for mobile
  if (DeviceManager.isMobile()) return null;
  return (
    <>
      <BannerHorizontalViewOnly onClose={onClose} show={show} />
      <ClickAwayListener mouseEvent={'onMouseUp'} onClickAway={handleClickAway}>
        <div className="handleClickAwayNotWorksWithoutThisWrap">
          <Root background={backgroundColor} opacity={backgroundOpacity} color={textColor}>
            <Header>
              <h2>{book?.description || translation?.expJournBook || 'Journey book'}</h2>
              <Icon.Close className="close-modal" onClick={onClose} fontSize="large" />
            </Header>
            <Content>
              <Main>
                {activePage ? (
                  activePage.url ? (
                    <img className="book" src={activePage.url} alt="" />
                  ) : (
                    <p className="page-description">{activePage.description}</p>
                  )
                ) : (
                  <Loader />
                )}
                <Footer
                  show={!!activePage?.moveToSweepUuid}
                  buttonColor={buttonColor}
                  color={textColor}
                >
                  <button onClick={() => handleMoveToSweep()}>
                    {translation?.experienceTeleport || 'Take me there'}{' '}
                    <Icon.ArrowRight fontSize="inherit" />
                  </button>
                </Footer>
              </Main>
              <List color={textColor} buttonColor={buttonColor}>
                {(pages || []).map((page) => (
                  <li
                    className={classnames(
                      'chapter',
                      isPageLocked(page.id) && 'locked',
                      !isPageLocked(page.id) && activePage.id === page.id && 'active',
                    )}
                    key={page.id}
                    onClick={() => changePage(page)}
                  >
                    {page.title}
                    {isPageLocked(page.id) && <Icon.Lock fontSize="inherit" />}
                  </li>
                ))}
              </List>
            </Content>
          </Root>
        </div>
      </ClickAwayListener>
    </>
  );
};
export default memo(GameBook);
